/**
 * MKS-94 — the project's billing terms on ProjectFormPage.
 *
 * The four terms are captured on the lead by sales and copied here on
 * conversion; from then on the project is the source of truth.
 *
 * Editable by admin, finance, or the project's PM (`canEdit`); everyone else
 * sees the same values read-only. The backend repeats the check on
 * PATCH /api/projects/:id/billing-terms — this is UI affordance, not the gate.
 *
 * Billing type is deliberately read-only here for everyone: it is the project's
 * `paymentTypeId`, owned by the admin-only template trio (and its lock) which
 * ProjectFormPage already edits through `ProjectFormTemplateSelector`.
 *
 * Saved through its own endpoint and its own button, separate from the page's
 * main save, because the audiences of the two differ.
 */
import { useEffect, useState } from 'react';
import { Button } from '../common/Button';
import { useUpdateProjectBillingTerms } from '../../hooks/useProjects';
import { usePaymentTerms } from '../../hooks/useLeads';
import { BILLING_FREQUENCY_LABELS } from '../../services/api/leads';
import type { Project } from '../../services/api/projects';

// Mirrors PROJECT_CONTRACT_FREQUENCIES in backend/src/services/billingTerms.ts.
// The three lead cadences come first — they are what conversion writes; the
// rest exist so an SLA project's inherited value stays selectable instead of
// silently resetting when someone saves this section.
const PROJECT_CONTRACT_FREQUENCIES = [
  'monthly',
  'quarterly',
  'per_phase',
  'yearly',
  'one_off',
  'perpetual',
] as const;

interface ProjectBillingTermsSectionProps {
  project: Project;
  canEdit: boolean;
}

export function ProjectBillingTermsSection({
  project,
  canEdit,
}: ProjectBillingTermsSectionProps) {
  const { data: paymentTermsData } = usePaymentTerms();
  const paymentTerms = paymentTermsData?.data ?? [];
  const updateMutation = useUpdateProjectBillingTerms();

  const [paymentTermId, setPaymentTermId] = useState('');
  const [contractFrequency, setContractFrequency] = useState('');
  const [contractUrl, setContractUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Re-seed whenever the project data changes (first load, or a refetch after
  // another surface edited the same columns).
  useEffect(() => {
    setPaymentTermId(project.paymentTermId ? String(project.paymentTermId) : '');
    setContractFrequency(project.contractFrequency ?? '');
    setContractUrl(project.contractUrl ?? '');
  }, [project.paymentTermId, project.contractFrequency, project.contractUrl]);

  const billingTypeLabel = project.paymentType?.name ?? '—';

  const handleSave = async () => {
    setError(null);
    try {
      await updateMutation.mutateAsync({
        id: project.id,
        input: {
          paymentTermId: paymentTermId ? parseInt(paymentTermId, 10) : null,
          contractFrequency: contractFrequency || null,
          contractUrl: contractUrl.trim() || null,
        },
      });
      setSavedAt(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save billing terms');
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
      <div>
        <h2 className="text-lg font-medium text-gray-900">Billing terms</h2>
        <p className="mt-1 text-sm text-gray-500">
          {canEdit
            ? 'Commercial terms for this project. Carried over from the lead on conversion.'
            : 'Commercial terms for this project. Only admins, finance and the project manager can change them.'}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <span className="block text-sm font-medium text-gray-700 mb-1">Billing type</span>
          <p className="text-sm text-gray-900 py-2" data-testid="billing-type-value">
            {billingTypeLabel}
          </p>
          <p className="text-xs text-gray-500">
            Set with the project template (admin only).
          </p>
        </div>

        <div>
          <label
            htmlFor="project-payment-term"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Invoice due days
          </label>
          {canEdit ? (
            <select
              id="project-payment-term"
              value={paymentTermId}
              onChange={(e) => setPaymentTermId(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">—</option>
              {paymentTerms.map((pt) => (
                <option key={pt.id} value={String(pt.id)}>
                  {pt.name} ({pt.days} days)
                </option>
              ))}
            </select>
          ) : (
            <p className="text-sm text-gray-900 py-2">
              {project.paymentTerm
                ? `${project.paymentTerm.name} (${project.paymentTerm.days} days)`
                : '—'}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="project-billing-frequency"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Billing frequency
          </label>
          {canEdit ? (
            <select
              id="project-billing-frequency"
              value={contractFrequency}
              onChange={(e) => setContractFrequency(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">—</option>
              {PROJECT_CONTRACT_FREQUENCIES.map((f) => (
                <option key={f} value={f}>
                  {BILLING_FREQUENCY_LABELS[f] ?? f}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-sm text-gray-900 py-2">
              {project.contractFrequency
                ? (BILLING_FREQUENCY_LABELS[project.contractFrequency] ?? project.contractFrequency)
                : '—'}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="project-contract-url"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Contract URL
          </label>
          {canEdit ? (
            <>
              <input
                id="project-contract-url"
                type="url"
                inputMode="url"
                placeholder="https://…"
                value={contractUrl}
                onChange={(e) => setContractUrl(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">http:// or https:// only.</p>
            </>
          ) : (
            <p className="text-sm text-gray-900 py-2">
              {project.contractUrl ? (
                <a
                  href={project.contractUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:underline break-all"
                >
                  {project.contractUrl}
                </a>
              ) : (
                '—'
              )}
            </p>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-red-700">{error}</p>}

      {canEdit && (
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={handleSave}
            isLoading={updateMutation.isPending}
          >
            Save billing terms
          </Button>
          {savedAt !== null && !updateMutation.isPending && !error && (
            <span className="text-sm text-green-700">Saved</span>
          )}
        </div>
      )}
    </div>
  );
}
